import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileSpreadsheet,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  Trash2,
  TrendingDown,
  TrendingUp,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR, fmtDateShort } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ReconOverhaulTab } from "./MarketplaceReconTab";

const FEE_LABEL = {
  admin: "Adm. (PPN)", komisi: "Komisi", layanan: "Layanan", proses: "Proses", pembayaran: "Pembayaran",
  transaksi: "Transaksi", gratis_ongkir: "Gratis Ongkir", logistik: "Logistik", kampanye: "Kampanye",
  pph: "PPh 22", voucher: "Voucher", lainnya: "Lainnya",
};
const MARKETPLACES = [{ value: "shopee", label: "Shopee" }, { value: "tiktok", label: "TikTok Shop" }];

const Stat = ({ label, value, tone = "" }) => (
  <div className="rounded-lg border border-taupe/20 bg-white px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-wmuted">{label}</div>
    <div className={`text-base font-bold ${tone}`}>{value}</div>
  </div>
);

const DateRangeFilter = ({ basis, setBasis, from, setFrom, to, setTo, showBasis = true, idp }) => (
  <div className="flex items-center gap-2 flex-wrap">
    {showBasis && (
      <Select value={basis} onValueChange={setBasis}>
        <SelectTrigger className="w-36 bg-white" data-testid={`${idp}-date-basis`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="transaksi">Tgl Transaksi</SelectItem>
          <SelectItem value="cair">Tgl Cair</SelectItem>
        </SelectContent>
      </Select>
    )}
    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-36 bg-white" data-testid={`${idp}-date-from`} />
    <span className="text-wmuted">—</span>
    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-36 bg-white" data-testid={`${idp}-date-to`} />
    {(from || to) && <Button size="sm" variant="outline" onClick={() => { setFrom(""); setTo(""); }} className="border-taupe text-cocoa" data-testid={`${idp}-date-reset`}>Reset</Button>}
  </div>
);

export default function MarketplaceSettlement() {
  const [batches, setBatches] = useState([]);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mp, setMp] = useState("shopee");
  const [unsettled, setUnsettled] = useState({ count: 0, items: [] });
  const [cfgOpen, setCfgOpen] = useState(false);
  const [batchSearch, setBatchSearch] = useState("");
  const [batchMarketplace, setBatchMarketplace] = useState("all");
  const [batchPage, setBatchPage] = useState(1);
  const fileRef = useRef();
  const batchPageSize = 10;

  const load = () => {
    api.get("/finance/settlements").then((r) => setBatches(r.data)).catch(() => {});
    api.get("/finance/unsettled", { params: { days: 30 } }).then((r) => setUnsettled(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    setBatchPage(1);
  }, [batchSearch, batchMarketplace, batches.length]);

  const filteredBatches = useMemo(() => {
    const keyword = batchSearch.trim().toLocaleLowerCase();

    return batches.filter((batch) => {
      const matchesMarketplace = (
        batchMarketplace === "all" || batch.marketplace === batchMarketplace
      );
      const searchable = [batch.filename, batch.marketplace, batch.period_start, batch.period_end]
        .join(" ")
        .toLocaleLowerCase();

      return matchesMarketplace && (!keyword || searchable.includes(keyword));
    });
  }, [batchMarketplace, batchSearch, batches]);
  const batchTotalPages = Math.max(1, Math.ceil(filteredBatches.length / batchPageSize));
  const currentBatchPage = Math.min(batchPage, batchTotalPages);
  const pageBatches = filteredBatches.slice(
    (currentBatchPage - 1) * batchPageSize,
    currentBatchPage * batchPageSize,
  );

  const resetBatchFilters = () => {
    setBatchSearch("");
    setBatchMarketplace("all");
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("marketplace", mp);
      const r = await api.post("/finance/settlements/upload", fd);
      toast.success(`Berhasil: ${r.data.row_count} transaksi diproses`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (b) => {
    if (!window.confirm(`Hapus batch "${b.filename}"? Bisa diupload ulang.`)) return;
    try { await api.delete(`/finance/settlements/${b.id}`); toast.success("Batch dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const normalizeDates = async () => {
    if (!window.confirm("Perbaiki format tanggal settlement lama (order_date/tgl cair) agar filter rentang tanggal akurat? Aman & bisa diulang.")) return;
    try {
      const r = await api.post("/finance/settlements/normalize-dates");
      toast.success(`Selesai: ${r.data.rows_fixed} baris diperbaiki dari ${r.data.scanned} baris.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (b) => {
    try {
      // Auto re-match (mis. sale dibuat setelah upload). Abaikan bila tak berizin.
      await api.post(`/finance/settlements/${b.id}/rematch`).catch(() => {});
      const r = await api.get(`/finance/settlements/${b.id}`);
      setDetail(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="settlement-page">
      <PageHeader title="Marketplace Settlement" subtitle="Rekonsiliasi pencairan marketplace: fee, nett vs HPP, dan biaya untuk Finance">
        <div className="flex items-center gap-2">
          <Select value={mp} onValueChange={setMp}>
            <SelectTrigger className="w-[150px]" data-testid="marketplace-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MARKETPLACES.map((m) => <SelectItem key={m.value} value={m.value} data-testid={`mp-opt-${m.value}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={upload} data-testid="settlement-file-input" />
          <Button variant="outline" onClick={() => setCfgOpen(true)} data-testid="settlement-settings-btn" className="border-cocoa text-cocoa"><Settings2 className="w-4 h-4 mr-1.5" /> Pengaturan</Button>
          <Button variant="outline" onClick={normalizeDates} data-testid="normalize-dates-btn" className="border-cocoa text-cocoa"><RefreshCw className="w-4 h-4 mr-1.5" /> Perbaiki Tanggal</Button>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-settlement-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">
            <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Memproses..." : "Upload Settlement"}
          </Button>
        </div>
      </PageHeader>

      <SummaryCards />
      {cfgOpen && <SettlementSettingsDialog onClose={() => setCfgOpen(false)} onSaved={() => api.get("/finance/unsettled", { params: { days: 30 } }).then((r) => setUnsettled(r.data)).catch(() => {})} />}

      <Tabs defaultValue="batches">
        <TabsList>
          <TabsTrigger value="batches" data-testid="tab-batches">Settlement</TabsTrigger>
          <TabsTrigger value="settled" data-testid="tab-settled">Sudah Settle</TabsTrigger>
          <TabsTrigger value="by-product" data-testid="tab-by-product">Per Produk</TabsTrigger>
          <TabsTrigger value="online" data-testid="tab-online">Listing Sales Online</TabsTrigger>
          <TabsTrigger value="recap" data-testid="tab-recap">Rekap Periode</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Order Ledger</TabsTrigger>
          <TabsTrigger value="recon-overhaul" data-testid="tab-recon-overhaul">Margin Produk (Baru)</TabsTrigger>
          <TabsTrigger value="unsettled" data-testid="tab-unsettled">
            Belum Settle {unsettled.count > 0 && <Badge className="ml-2 bg-status-review text-white">{unsettled.count}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="batches" className="mt-4">
          <Panel>
            <div
              className="grid gap-2 border-b border-taupe/20 p-4 sm:grid-cols-[minmax(220px,1fr)_180px_auto]"
              data-testid="settlement-batch-datatable-controls"
            >
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-wmuted" />
                <Input
                  className="pl-9"
                  data-testid="settlement-batch-search"
                  onChange={(event) => setBatchSearch(event.target.value)}
                  placeholder="Cari nama file atau periode"
                  value={batchSearch}
                />
              </div>
              <Select onValueChange={setBatchMarketplace} value={batchMarketplace}>
                <SelectTrigger data-testid="settlement-batch-marketplace-filter">
                  <SelectValue placeholder="Semua marketplace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua marketplace</SelectItem>
                  {MARKETPLACES.map((marketplace) => (
                    <SelectItem key={marketplace.value} value={marketplace.value}>
                      {marketplace.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="settlement-batch-reset-filter"
                onClick={resetBatchFilters}
                size="icon"
                title="Reset filter"
                variant="outline"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
            <p className="px-4 pb-3 pt-3 text-xs text-wmuted" data-testid="settlement-batch-result-count">
              Menampilkan {pageBatches.length} dari {filteredBatches.length} batch settlement
            </p>
            {batches.length === 0 ? (
              <div className="text-center py-12 text-wmuted">
                <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-40" />
                Belum ada settlement. Upload file Excel dari Shopee untuk mulai.
              </div>
            ) : filteredBatches.length === 0 ? (
              <div className="py-12 text-center text-wmuted" data-testid="settlement-batch-no-results">
                Tidak ada batch settlement yang sesuai filter.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>File</TableHead>
                    <TableHead>Periode Cair</TableHead>
                    <TableHead className="text-center">Trx</TableHead>
                    <TableHead className="text-right">Penjualan</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead className="text-right">Nett Cair</TableHead>
                    <TableHead className="text-right">HPP</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageBatches.map((b) => (
                    <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                      <TableCell className="font-medium text-cocoa max-w-[220px] truncate">
                        <span className="inline-flex items-center gap-1.5">
                          <Badge variant="outline" className="text-[10px] uppercase">{b.marketplace}</Badge>
                          <span className="truncate">{b.filename}</span>
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-wmuted">{fmtDateShort(b.period_start)} — {fmtDateShort(b.period_end)}</TableCell>
                      <TableCell className="text-center">{b.row_count}<span className="text-xs text-wmuted"> ({b.totals.unmatched} tak cocok)</span></TableCell>
                      <TableCell className="text-right">{fmtIDR(b.totals.gross)}</TableCell>
                      <TableCell className="text-right text-red-500">-{fmtIDR(b.totals.fee)}</TableCell>
                      <TableCell className="text-right font-semibold">{fmtIDR(b.totals.nett)}</TableCell>
                      <TableCell className="text-right text-wmuted">{fmtIDR(b.totals.hpp)}</TableCell>
                      <TableCell className={`text-right font-bold ${b.totals.profit >= 0 ? "text-status-passed" : "text-red-500"}`}>{fmtIDR(b.totals.profit)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => openDetail(b)} data-testid={`detail-batch-${b.id}`}><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => del(b)} data-testid={`delete-batch-${b.id}`} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div
              className="flex flex-wrap items-center justify-between gap-3 border-t border-taupe/20 p-4"
              data-testid="settlement-batch-pagination"
            >
              <p className="text-xs text-wmuted" data-testid="settlement-batch-pagination-info">
                Halaman {currentBatchPage} dari {batchTotalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="settlement-batch-previous-page"
                  disabled={currentBatchPage <= 1}
                  onClick={() => setBatchPage((value) => Math.max(1, value - 1))}
                  size="sm"
                  variant="outline"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Sebelumnya
                </Button>
                <Button
                  data-testid="settlement-batch-next-page"
                  disabled={currentBatchPage >= batchTotalPages}
                  onClick={() => setBatchPage((value) => Math.min(batchTotalPages, value + 1))}
                  size="sm"
                  variant="outline"
                >
                  Berikutnya
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="settled" className="mt-4">
          <SettledTab />
        </TabsContent>

        <TabsContent value="by-product" className="mt-4">
          <ByProductTab />
        </TabsContent>

        <TabsContent value="online" className="mt-4">
          <OnlineSalesTab />
        </TabsContent>

        <TabsContent value="recap" className="mt-4">
          <PeriodRecapTab />
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          <OrderLedgerTab />
        </TabsContent>

        <TabsContent value="recon-overhaul" className="mt-4">
          <ReconOverhaulTab />
        </TabsContent>

        <TabsContent value="unsettled" className="mt-4">
          <UnsettledTab />
        </TabsContent>
      </Tabs>

      <SettlementDetail detail={detail} setDetail={setDetail} onChanged={load} />
    </div>
  );
}

function ByProductTab() {
  const [q, setQ] = useState("");
  const [mp, setMp] = useState("all");
  const [dbasis, setDbasis] = useState("transaksi");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [data, setData] = useState({ total: 0, pages: 0, items: [], totals: {} });
  const [openKey, setOpenKey] = useState(null);
  const [txns, setTxns] = useState({});
  const [txLoading, setTxLoading] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/finance/settled/by-product", { params: { q: q || undefined, marketplace: mp, date_from: dfrom || undefined, date_to: dto || undefined, date_basis: dbasis, page, limit } })
        .then((r) => setData(r.data)).catch(() => {});
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, mp, dbasis, dfrom, dto, page, limit]);
  useEffect(() => { setPage(1); setOpenKey(null); setTxns({}); }, [q, mp, dbasis, dfrom, dto]);
  const pctColor = (v) => (v >= 0 ? "text-status-passed" : "text-red-500");
  const toggle = (product) => {
    if (openKey === product) { setOpenKey(null); return; }
    setOpenKey(product);
    if (!txns[product]) {
      setTxLoading(true);
      api.get("/finance/settled/by-product/transactions", { params: { product, marketplace: mp, date_from: dfrom || undefined, date_to: dto || undefined, date_basis: dbasis } })
        .then((r) => setTxns((c) => ({ ...c, [product]: r.data }))).catch(() => {}).finally(() => setTxLoading(false));
    }
  };
  return (
    <Panel>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk..." className="w-72 bg-white" data-testid="byproduct-search" />
        <Select value={mp} onValueChange={setMp}>
          <SelectTrigger className="w-40 bg-white" data-testid="byproduct-mp-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Marketplace</SelectItem>
            {MARKETPLACES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangeFilter basis={dbasis} setBasis={setDbasis} from={dfrom} setFrom={setDfrom} to={dto} setTo={setDto} idp="byproduct" />
        <span className="ml-auto text-sm text-wmuted" data-testid="byproduct-total">{data.total} produk</span>
      </div>
      <div className="flex gap-4 mb-3 flex-wrap" data-testid="byproduct-totals">
        <div className="px-4 py-2 rounded-lg bg-cocoa/5 text-sm"><span className="text-wmuted">Transaksi: </span><b className="text-cocoa">{data.totals?.count || 0}</b></div>
        <div className="px-4 py-2 rounded-lg bg-status-passed/10 text-sm"><span className="text-wmuted">Total Nett: </span><b className="text-status-passed">{fmtIDR(data.totals?.nett)}</b></div>
        <div className="px-4 py-2 rounded-lg bg-cocoa/5 text-sm"><span className="text-wmuted">Total Profit: </span><b className="text-cocoa">{fmtIDR(data.totals?.profit)}</b></div>
      </div>
      {data.items.length === 0 ? (
        <div className="text-center py-10 text-wmuted">Belum ada data settlement per produk.</div>
      ) : (
        <>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Produk</TableHead><TableHead className="text-right">Transaksi</TableHead>
              <TableHead className="text-right">Kotor</TableHead><TableHead className="text-right">Nett</TableHead>
              <TableHead className="text-right">% Cair</TableHead><TableHead className="text-right">HPP</TableHead>
              <TableHead className="text-right">% HPP</TableHead><TableHead className="text-right">Profit</TableHead>
              <TableHead className="text-right">% Profit</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.items.flatMap((r) => {
                const rowEl = (
                <TableRow key={r.product} data-testid={`byproduct-row-${r.product}`}>
                  <TableCell className="text-sm font-medium text-cocoa max-w-[240px] cursor-pointer" title={r.product} onClick={() => toggle(r.product)} data-testid={`byproduct-expand-${r.product}`}>
                    <span className="mr-1 inline-block w-3 text-wmuted">{openKey === r.product ? "▾" : "▸"}</span><span className="align-middle">{r.product}</span>
                  </TableCell>
                  <TableCell className="text-right text-sm">{r.count}</TableCell>
                  <TableCell className="text-right text-sm">{fmtIDR(r.gross)}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{fmtIDR(r.nett)}</TableCell>
                  <TableCell className="text-right text-sm text-wmuted">{r.pct_cair}%</TableCell>
                  <TableCell className="text-right text-sm text-wmuted">{fmtIDR(r.hpp)}</TableCell>
                  <TableCell className="text-right text-sm text-wmuted">{r.pct_hpp}%</TableCell>
                  <TableCell className={`text-right text-sm font-semibold ${pctColor(r.profit)}`}>{fmtIDR(r.profit)}</TableCell>
                  <TableCell className={`text-right text-sm ${pctColor(r.pct_profit)}`}>{r.pct_profit}%</TableCell>
                </TableRow>);
                if (openKey !== r.product) return [rowEl];
                const det = txns[r.product];
                const exp = (
                <TableRow key={r.product + "-exp"} className="hover:bg-transparent">
                  <TableCell colSpan={9} className="bg-cocoa/5 p-0">
                    {(!det && txLoading) ? <div className="p-3 text-sm text-wmuted">Memuat transaksi…</div> :
                      (det?.items?.length ? (
                        <div className="p-3" data-testid={`byproduct-txns-${r.product}`}>
                          <div className="text-xs text-wmuted mb-1">{det.count} transaksi memuat "{r.product}"</div>
                          <table className="w-full text-xs">
                            <thead><tr className="text-left text-wmuted"><th className="py-1 pr-2">Tgl Transaksi</th><th className="pr-2">Order ID</th><th className="pr-2 text-right">Qty</th><th className="pr-2 text-right">Kotor</th><th className="pr-2 text-right">Nett Cair</th><th className="pr-2 text-right">HPP</th><th className="pr-2 text-right">Profit</th><th className="text-right">Status</th></tr></thead>
                            <tbody>
                              {det.items.map((t, i) => (
                                <tr key={i} className="border-t border-taupe/20">
                                  <td className="py-1 pr-2">{t.date || "-"}</td>
                                  <td className="pr-2 font-mono">{t.order_id || "-"}{t.size ? <span className="ml-1 text-wmuted">({t.size})</span> : ""}</td>
                                  <td className="pr-2 text-right">{t.qty || "-"}</td>
                                  <td className="pr-2 text-right">{fmtIDR(t.gross)}</td>
                                  <td className="pr-2 text-right font-medium">{fmtIDR(t.nett)}</td>
                                  <td className="pr-2 text-right text-wmuted">{fmtIDR(t.hpp)}</td>
                                  <td className={`pr-2 text-right ${pctColor(t.profit)}`}>{fmtIDR(t.profit)}</td>
                                  <td className="text-right"><span className={t.status === "Return" ? "text-red-500" : "text-status-passed"}>{t.status}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : <div className="p-3 text-sm text-wmuted">Tidak ada transaksi.</div>)}
                  </TableCell>
                </TableRow>);
                return [rowEl, exp];
              })}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-wmuted">Halaman {data.page} / {data.pages || 1} · {data.total} produk</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-taupe text-cocoa" data-testid="byproduct-prev">Sebelumnya</Button>
              <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage((p) => p + 1)} className="border-taupe text-cocoa" data-testid="byproduct-next">Berikutnya</Button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

function SummaryCards() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [s, setS] = useState({ online_total: 0, settled: 0, unsettled: 0, sum_nett: 0, sum_profit: 0 });
  useEffect(() => {
    api.get("/finance/summary", { params: { start: start || undefined, end: end || undefined } })
      .then((r) => setS(r.data)).catch(() => {});
  }, [start, end]);
  const cards = [
    { label: "Transaksi Online", val: s.online_total, cls: "text-cocoa", tid: "sum-online" },
    { label: "Sudah Settle", val: s.settled, cls: "text-status-passed", tid: "sum-settled" },
    { label: "Belum Settle", val: s.unsettled, cls: "text-status-review", tid: "sum-unsettled" },
    { label: "Total Nett", val: fmtIDR(s.sum_nett), cls: "text-cocoa", tid: "sum-nett" },
    { label: "Total Profit", val: fmtIDR(s.sum_profit), cls: "text-status-passed", tid: "sum-profit" },
  ];
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-sm text-wmuted">Periode:</span>
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40 bg-white" data-testid="summary-start" />
        <span className="text-wmuted">—</span>
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40 bg-white" data-testid="summary-end" />
        {(start || end) && <Button size="sm" variant="outline" onClick={() => { setStart(""); setEnd(""); }} className="border-taupe text-cocoa" data-testid="summary-reset">Reset</Button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <Panel key={c.tid} className="p-4" >
            <div className="text-xs font-semibold text-wmuted uppercase tracking-wide">{c.label}</div>
            <div className={`mt-1 text-xl font-heading font-bold ${c.cls}`} data-testid={c.tid}>{c.val}</div>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function SettledTab() {
  const [q, setQ] = useState("");
  const [mp, setMp] = useState("all");
  const [dbasis, setDbasis] = useState("transaksi");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [data, setData] = useState({ total: 0, pages: 0, items: [] });

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/finance/settled", { params: { q: q || undefined, marketplace: mp, date_from: dfrom || undefined, date_to: dto || undefined, date_basis: dbasis, page, limit } })
        .then((r) => setData(r.data)).catch(() => {});
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, mp, dbasis, dfrom, dto, page, limit]);
  useEffect(() => { setPage(1); }, [q, mp, dbasis, dfrom, dto]);

  return (
    <Panel>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Order ID / No. Sale / produk..." className="w-72 bg-white" data-testid="settled-search" />
        <Select value={mp} onValueChange={setMp}>
          <SelectTrigger className="w-40 bg-white" data-testid="settled-mp-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Marketplace</SelectItem>
            {MARKETPLACES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <DateRangeFilter basis={dbasis} setBasis={setDbasis} from={dfrom} setFrom={setDfrom} to={dto} setTo={setDto} idp="settled" />
        <span className="ml-auto text-sm text-wmuted" data-testid="settled-total">{data.total} transaksi cocok</span>
        <Button size="sm" variant="outline" onClick={() => api.get("/finance/settled/export", { params: { q: q || undefined, marketplace: mp, date_from: dfrom || undefined, date_to: dto || undefined, date_basis: dbasis }, responseType: "blob" }).then((r) => { const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "sudah-settle.xlsx"; a.click(); URL.revokeObjectURL(url); }).catch(() => toast.error("Export gagal"))} className="border-status-passed text-status-passed" data-testid="settled-export-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Export Excel</Button>
      </div>
      {data.items.length === 0 ? (
        <div className="text-center py-10 text-wmuted">Belum ada transaksi yang tercocokkan.</div>
      ) : (
        <>
          <div className="flex gap-4 mb-3 flex-wrap" data-testid="settled-totals">
            <div className="px-4 py-2 rounded-lg bg-status-passed/10 text-sm"><span className="text-wmuted">Total Nett: </span><b className="text-status-passed">{fmtIDR(data.totals?.nett)}</b></div>
            <div className="px-4 py-2 rounded-lg bg-cocoa/5 text-sm"><span className="text-wmuted">Total Profit: </span><b className="text-cocoa">{fmtIDR(data.totals?.profit)}</b></div>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Order ID</TableHead><TableHead>No. Sale</TableHead><TableHead>Produk</TableHead>
                <TableHead>Marketplace</TableHead><TableHead className="text-right">Kotor</TableHead>
                <TableHead className="text-right">Fee</TableHead><TableHead className="text-right">Nett</TableHead>
                <TableHead className="text-right">HPP</TableHead><TableHead className="text-right">% HPP</TableHead><TableHead className="text-right">Profit</TableHead><TableHead className="text-right">% Profit</TableHead>
                <TableHead>Periode</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((r) => (
                <TableRow key={r.id} data-testid={`settled-row-${r.id}`}>
                  <TableCell className="text-sm">{r.order_id}</TableCell>
                  <TableCell className="text-sm font-medium text-cocoa">{r.matched_sale_number || "-"}</TableCell>
                  <TableCell className="text-sm max-w-[220px] truncate" title={r.matched_products || r.product_name}>{r.matched_products || r.product_name || "-"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] uppercase">{r.marketplace}</Badge></TableCell>
                  <TableCell className="text-right text-sm">{fmtIDR(r.gross)}</TableCell>
                  <TableCell className="text-right text-sm text-red-500">-{fmtIDR(r.total_fee)}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{fmtIDR(r.nett)}</TableCell>
                  <TableCell className="text-right text-sm text-wmuted">{fmtIDR(r.hpp)}</TableCell>
                  <TableCell className="text-right text-sm text-wmuted">{r.nett ? Math.round((r.hpp / r.nett) * 100) : 0}%</TableCell>
                  <TableCell className={`text-right text-sm font-semibold ${r.profit >= 0 ? "text-status-passed" : "text-red-500"}`}>{fmtIDR(r.profit)}</TableCell>
                  <TableCell className={`text-right text-sm ${r.profit >= 0 ? "text-status-passed" : "text-red-500"}`}>{r.nett ? Math.round((r.profit / r.nett) * 100) : 0}%</TableCell>
                  <TableCell className="text-sm text-wmuted">{fmtDateShort(r.period_end)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-wmuted">Halaman {data.page} / {data.pages || 1} · {data.total} data</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-taupe text-cocoa" data-testid="settled-prev">Sebelumnya</Button>
              <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage((p) => p + 1)} className="border-taupe text-cocoa" data-testid="settled-next">Berikutnya</Button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

function OnlineSalesTab() {
  const [q, setQ] = useState("");
  const [mp, setMp] = useState("all");
  const [status, setStatus] = useState("all");
  const [dbasis, setDbasis] = useState("transaksi");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [sort, setSort] = useState("tanggal");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState({ total: 0, cair: 0, belum: 0, page: 1, pages: 0, limit: 25, items: [], totals: {} });

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/finance/online-sales", { params: { q: q || undefined, marketplace: mp, status, date_from: dfrom || undefined, date_to: dto || undefined, date_basis: dbasis, sort, dir, page, limit } })
        .then((r) => setData(r.data)).catch(() => {});
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, mp, status, dbasis, dfrom, dto, sort, dir, page, limit]);
  useEffect(() => { setPage(1); }, [q, mp, status, dbasis, dfrom, dto, sort, dir]);

  const toggleSort = (f) => { if (sort === f) setDir((d) => (d === "asc" ? "desc" : "asc")); else { setSort(f); setDir("asc"); } };
  const Th = ({ f, children, align = "left" }) => (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button type="button" onClick={() => toggleSort(f)} className={`inline-flex items-center gap-1 hover:text-cocoa ${sort === f ? "text-cocoa font-semibold" : ""}`} data-testid={`online-sort-${f}`}>
        {children}{sort === f ? (dir === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </TableHead>
  );
  const mpBadge = (m) => {
    const map = { shopee: "bg-orange-100 text-orange-700", tiktok: "bg-neutral-900 text-white", tokopedia: "bg-green-100 text-green-700" };
    return <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded self-start ${map[m] || "bg-taupe/20 text-wmuted"}`}>{m}</span>;
  };
  const dash = <span className="text-wmuted">—</span>;

  return (
    <Panel>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Order ID / Sales ID / customer..." className="w-64 bg-white" data-testid="online-search" />
        <Select value={mp} onValueChange={setMp}>
          <SelectTrigger className="w-40 bg-white" data-testid="online-mp-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Marketplace</SelectItem>
            {MARKETPLACES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 bg-white" data-testid="online-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="cair">Sudah Cair</SelectItem>
            <SelectItem value="belum">Belum Cair</SelectItem>
          </SelectContent>
        </Select>
        <DateRangeFilter basis={dbasis} setBasis={setDbasis} from={dfrom} setFrom={setDfrom} to={dto} setTo={setDto} idp="online" />
        <Button size="sm" variant="outline" onClick={() => api.get("/finance/online-sales/export", { params: { q: q || undefined, marketplace: mp, status, date_from: dfrom || undefined, date_to: dto || undefined, date_basis: dbasis, sort, dir }, responseType: "blob" }).then((r) => { const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "listing-sales-online.xlsx"; a.click(); URL.revokeObjectURL(url); }).catch(() => toast.error("Export gagal"))} className="border-status-passed text-status-passed ml-auto" data-testid="online-export-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Export Excel</Button>
      </div>
      <div className="flex gap-3 mb-3 flex-wrap">
        <Stat label="Total Online" value={data.total} />
        <Stat label="Sudah Cair" value={data.cair} tone="text-status-passed" />
        <Stat label="Belum Cair" value={data.belum} tone="text-status-review" />
        <Stat label="Total Net Cair" value={fmtIDR(data.totals?.nett)} tone="text-status-passed" />
        <Stat label="Total Nett Kotor (Cair)" value={fmtIDR(data.totals?.kotor_cair)} tone="text-cocoa" />
        <Stat label="Total Kotor (Semua)" value={fmtIDR(data.totals?.kotor_all)} tone="text-cocoa" />
        <Stat label="% Net Cair vs Kotor" value={`${data.totals?.pct_nett_cair ?? 0}%`} tone="text-status-passed" />
        <Stat label="Total HPP (Cair)" value={fmtIDR(data.totals?.hpp_cair)} tone="text-cocoa" />
        <Stat label="% Cair vs HPP" value={`${data.totals?.pct_cair_vs_hpp ?? 0}%`} tone="text-status-passed" />
        <Stat label="% Profit vs HPP" value={`${data.totals?.pct_profit_vs_hpp ?? 0}%`} tone="text-status-passed" />
        <Stat label="Total Belum Cair (Rp)" value={fmtIDR(data.totals?.belum_value)} tone="text-status-review" />
        <Stat label="HPP Belum Cair" value={data.totals?.belum_hpp != null ? fmtIDR(data.totals.belum_hpp) : "Filter periode dulu"} tone="text-status-review" />
        <Stat label="Total Return" value={fmtIDR(data.totals?.return)} tone="text-red-500" />
        <Stat label="Total Profit (Cair)" value={fmtIDR(data.totals?.profit)} tone="text-cocoa" />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>No</TableHead>
              <Th f="order_id">Order ID</Th>
              <Th f="sales_id">Sales ID</Th>
              <Th f="tanggal">Tgl Transaksi</Th>
              <Th f="customer">Customer</Th>
              <Th f="total_item" align="right">Total Item</Th>
              <Th f="hpp" align="right">HPP</Th>
              <Th f="kotor" align="right">Harga Kotor</Th>
              <Th f="fee" align="right">Fee</Th>
              <Th f="nett" align="right">Harga Net (Cair)</Th>
              <Th f="pct_hpp" align="right">% HPP</Th>
              <Th f="profit" align="right">Profit</Th>
              <Th f="pct_profit" align="right">% Profit</Th>
              <Th f="status">Status</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 ? (
              <TableRow><TableCell colSpan={14} className="text-center py-10 text-wmuted">Tidak ada data.</TableCell></TableRow>
            ) : data.items.map((r, i) => (
              <TableRow key={r.sale_id} data-testid={`online-row-${r.sale_id}`}>
                <TableCell className="text-sm text-wmuted">{(data.page - 1) * data.limit + i + 1}</TableCell>
                <TableCell className="text-sm font-mono whitespace-nowrap">{r.order_id}</TableCell>
                <TableCell className="text-sm font-medium text-cocoa whitespace-nowrap">{r.sales_number || "-"}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{r.created_at ? fmtDateShort(r.created_at) : "-"}</TableCell>
                <TableCell className="text-sm"><div className="flex flex-col gap-0.5 min-w-[140px]"><span>{r.customer_name || "-"}</span>{mpBadge(r.marketplace)}</div></TableCell>
                <TableCell className="text-right text-sm">{r.total_item}</TableCell>
                <TableCell className="text-right text-sm text-wmuted whitespace-nowrap">{r.hpp != null ? fmtIDR(r.hpp) : dash}</TableCell>
                <TableCell className="text-right text-sm whitespace-nowrap">{fmtIDR(r.gross)}</TableCell>
                <TableCell className="text-right text-sm text-red-500 whitespace-nowrap">{r.fee != null ? `-${fmtIDR(r.fee)}` : dash}</TableCell>
                <TableCell className="text-right text-sm font-medium whitespace-nowrap">{r.nett != null ? fmtIDR(r.nett) : dash}</TableCell>
                <TableCell className="text-right text-sm text-wmuted">{r.pct_hpp != null ? `${r.pct_hpp}%` : dash}</TableCell>
                <TableCell className={`text-right text-sm font-semibold whitespace-nowrap ${r.status === "cair" && r.profit >= 0 ? "text-status-passed" : r.status === "cair" ? "text-red-500" : ""}`}>{r.profit != null ? fmtIDR(r.profit) : dash}</TableCell>
                <TableCell className={`text-right text-sm ${r.status === "cair" && r.profit >= 0 ? "text-status-passed" : r.status === "cair" ? "text-red-500" : ""}`}>{r.pct_profit != null ? `${r.pct_profit}%` : dash}</TableCell>
                <TableCell>{r.status === "cair" ? <Badge className="bg-status-passed text-white">Cair</Badge> : <Badge className="bg-status-review text-white whitespace-nowrap">Belum Cair</Badge>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between mt-3 text-sm flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-wmuted">Tampilkan</span>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="w-20 bg-white" data-testid="online-limit"><SelectValue /></SelectTrigger>
            <SelectContent>{[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <span className="text-wmuted">· Hal {data.page} / {data.pages || 1} · {data.total} data</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-taupe text-cocoa" data-testid="online-prev">Sebelumnya</Button>
          <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage((p) => p + 1)} className="border-taupe text-cocoa" data-testid="online-next">Berikutnya</Button>
        </div>
      </div>
    </Panel>
  );
}

function PeriodRecapTab() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [mp, setMp] = useState("all");
  const [d, setD] = useState({ total: {}, per_marketplace: [] });
  useEffect(() => {
    api.get("/finance/period-recap", { params: { month, marketplace: mp } }).then((r) => setD(r.data)).catch(() => {});
  }, [month, mp]);
  const t = d.total || {};
  const cards = [
    { l: "Total Cair (Net)", v: fmtIDR(t.total_cair), c: "text-status-passed" },
    { l: "Nilai Return", v: fmtIDR(t.nilai_return), c: "text-red-500" },
    { l: "HPP", v: fmtIDR(t.hpp_jual), c: "text-cocoa" },
    { l: "HPP Return", v: fmtIDR(t.hpp_return), c: "text-red-500" },
    { l: "Profit Kotor", v: fmtIDR(t.profit_kotor), c: "text-status-passed" },
    { l: "% Profit Kotor", v: `${t.pct_profit_kotor ?? 0}%`, c: "text-status-passed" },
    { l: "Total Kotor", v: fmtIDR(t.kotor), c: "text-cocoa" },
    { l: "% Net Cair vs Kotor", v: `${t.pct_nett_cair ?? 0}%`, c: "text-status-passed" },
    { l: "% Cair vs HPP", v: `${t.pct_cair_vs_hpp ?? 0}%`, c: "text-status-passed" },
    { l: "% Profit vs HPP", v: `${t.pct_profit_vs_hpp ?? 0}%`, c: "text-status-passed" },
    { l: "Total Fee", v: fmtIDR(t.fee), c: "text-red-500" },
    { l: "Jumlah Transaksi", v: t.count ?? 0, c: "text-cocoa" },
    { l: "Belum Dikasir", v: `${t.belum_count ?? 0} (${fmtIDR(t.belum_nilai)})`, c: "text-status-review" },
  ];
  return (
    <Panel>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm text-wmuted">Bulan:</span>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" data-testid="recap-month" />
        <Select value={mp} onValueChange={setMp}>
          <SelectTrigger className="w-40 bg-white" data-testid="recap-mp"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Marketplace</SelectItem>
            {MARKETPLACES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-wmuted">Basis: Tanggal Transaksi</span>
        <Button size="sm" variant="outline" onClick={() => api.get("/finance/period-recap/export", { params: { month, marketplace: mp }, responseType: "blob" }).then((r) => { const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `rekap-${month}.xlsx`; a.click(); URL.revokeObjectURL(url); }).catch(() => toast.error("Export gagal"))} className="border-status-passed text-status-passed ml-auto" data-testid="recap-export"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Export Excel</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        {cards.map((c, i) => (
          <Panel key={c.l} className="p-4">
            <div className="text-[11px] font-semibold text-wmuted uppercase tracking-wide">{c.l}</div>
            <div className={`mt-1 text-lg font-heading font-bold ${c.c}`} data-testid={`recap-card-${i}`}>{c.v}</div>
          </Panel>
        ))}
      </div>
      {(d.per_marketplace || []).length > 0 && (
        <>
          <div className="text-sm font-semibold text-cocoa mb-2">Rincian per Marketplace</div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Marketplace</TableHead><TableHead className="text-right">Transaksi</TableHead>
                <TableHead className="text-right">Total Cair</TableHead><TableHead className="text-right">Return</TableHead>
                <TableHead className="text-right">HPP</TableHead><TableHead className="text-right">HPP Return</TableHead>
                <TableHead className="text-right">Profit Kotor</TableHead><TableHead className="text-right">% Profit</TableHead>
                <TableHead className="text-right">Belum Dikasir</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {d.per_marketplace.map((r) => (
                  <TableRow key={r.marketplace} data-testid={`recap-mp-${r.marketplace}`}>
                    <TableCell><Badge variant="outline" className="uppercase text-[10px]">{r.marketplace}</Badge></TableCell>
                    <TableCell className="text-right text-sm">{r.count}</TableCell>
                    <TableCell className="text-right text-sm font-medium">{fmtIDR(r.total_cair)}</TableCell>
                    <TableCell className="text-right text-sm text-red-500">{fmtIDR(r.nilai_return)}</TableCell>
                    <TableCell className="text-right text-sm">{fmtIDR(r.hpp_jual)}</TableCell>
                    <TableCell className="text-right text-sm text-red-500">{fmtIDR(r.hpp_return)}</TableCell>
                    <TableCell className="text-right text-sm font-semibold text-status-passed">{fmtIDR(r.profit_kotor)}</TableCell>
                    <TableCell className="text-right text-sm text-status-passed">{r.pct_profit_kotor}%</TableCell>
                    <TableCell className="text-right text-sm text-status-review">{r.belum_count} ({fmtIDR(r.belum_nilai)})</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </Panel>
  );
}

function OrderLedgerTab() {
  const [q, setQ] = useState(""); const [mp, setMp] = useState("all"); const [cc, setCc] = useState("all");
  const [dbasis, setDbasis] = useState("transaksi"); const [dfrom, setDfrom] = useState(""); const [dto, setDto] = useState("");
  const [sort, setSort] = useState("tanggal"); const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1); const [limit] = useState(25);
  const [d, setD] = useState({ total: 0, page: 1, pages: 0, limit: 25, items: [], summary: {} });
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/finance/order-ledger", { params: { q: q || undefined, marketplace: mp, crosscheck: cc, date_from: dfrom || undefined, date_to: dto || undefined, date_basis: dbasis, sort, dir, page, limit } })
        .then((r) => setD(r.data)).catch(() => {});
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, mp, cc, dbasis, dfrom, dto, sort, dir, page, limit]);
  useEffect(() => { setPage(1); }, [q, mp, cc, dbasis, dfrom, dto, sort, dir]);
  const toggleSort = (f) => { if (sort === f) setDir((x) => (x === "asc" ? "desc" : "asc")); else { setSort(f); setDir("asc"); } };
  const Th = ({ f, children, align = "left" }) => (
    <TableHead className={align === "right" ? "text-right" : ""}>
      <button type="button" onClick={() => toggleSort(f)} className={`inline-flex items-center gap-1 hover:text-cocoa ${sort === f ? "text-cocoa font-semibold" : ""}`} data-testid={`ledger-sort-${f}`}>{children}{sort === f ? (dir === "asc" ? " ↑" : " ↓") : ""}</button>
    </TableHead>
  );
  const s = d.summary || {};
  return (
    <Panel>
      <div className="flex items-start gap-2 mb-3 text-sm text-cocoa">
        <AlertTriangle className="w-4 h-4 text-status-review mt-0.5" />
        <span>Order yang <b>terdeteksi di pencairan (settlement) tapi belum dikasir</b>. Sumber tunggal: status kasir terkini + status tinjauan tim pengiriman. Yang sudah dikasir belakangan otomatis ditandai <b>selesai</b>.</span>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Order ID..." className="w-56 bg-white" data-testid="ledger-search" />
        <Select value={mp} onValueChange={setMp}><SelectTrigger className="w-36 bg-white" data-testid="ledger-mp"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Semua Marketplace</SelectItem>{MARKETPLACES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select>
        <Select value={cc} onValueChange={setCc}><SelectTrigger className="w-44 bg-white" data-testid="ledger-cc"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Semua Crosscheck</SelectItem><SelectItem value="reviewed">Sudah Ditinjau</SelectItem><SelectItem value="pending">Perlu Ditinjau</SelectItem><SelectItem value="none">Tak Ada di Crosscheck</SelectItem></SelectContent></Select>
        <DateRangeFilter basis={dbasis} setBasis={setDbasis} from={dfrom} setFrom={setDfrom} to={dto} setTo={setDto} idp="ledger" />
      </div>
      <div className="flex gap-3 mb-3 flex-wrap">
        <Stat label="Belum Dikasir" value={s.belum_dikasir ?? 0} tone="text-status-review" />
        <Stat label="Sudah Dikasir (selesai)" value={s.sudah_dikasir ?? 0} tone="text-status-passed" />
        <Stat label="Sudah Ditinjau Pengiriman" value={s.reviewed ?? 0} tone="text-cocoa" />
        <Stat label="Total Order" value={s.total ?? 0} />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>No</TableHead><Th f="order_id">Order ID</Th><Th f="marketplace">Marketplace</Th>
            <Th f="cair" align="right">Nilai Cair</Th><Th f="kotor" align="right">Harga Kotor</Th>
            <Th f="tanggal">Tgl Transaksi</Th><TableHead>Status Kasir</TableHead><TableHead>Crosscheck Pengiriman</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-wmuted">Tidak ada order belum dikasir 🎉</TableCell></TableRow>
            ) : d.items.map((r, i) => (
              <TableRow key={r.order_id} data-testid={`ledger-row-${r.order_id}`}>
                <TableCell className="text-sm text-wmuted">{(d.page - 1) * d.limit + i + 1}</TableCell>
                <TableCell className="text-sm font-mono whitespace-nowrap">{r.order_id}</TableCell>
                <TableCell><Badge variant="outline" className="uppercase text-[10px]">{r.marketplace}</Badge></TableCell>
                <TableCell className="text-right text-sm font-medium whitespace-nowrap">{fmtIDR(r.nett)}</TableCell>
                <TableCell className="text-right text-sm whitespace-nowrap">{fmtIDR(r.gross)}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{r.order_date ? fmtDateShort(r.order_date) : "-"}</TableCell>
                <TableCell>{r.kasir_status === "dikasir" ? <Badge className="bg-status-passed text-white whitespace-nowrap">Sudah Dikasir{r.sales_number ? ` (${r.sales_number})` : ""}</Badge> : <Badge className="bg-status-review text-white whitespace-nowrap">Belum Dikasir</Badge>}</TableCell>
                <TableCell className="text-sm">{r.crosscheck_status === "reviewed" ? <span className="text-status-passed">Sudah ditinjau{r.reviewed_by ? ` · ${r.reviewed_by}` : ""}</span> : r.crosscheck_status === "pending" ? <span className="text-status-review">Perlu ditinjau</span> : <span className="text-wmuted">Tak ada di crosscheck</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between mt-3 text-sm flex-wrap gap-2">
        <span className="text-wmuted">Hal {d.page} / {d.pages || 1} · {d.total} order</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-taupe text-cocoa" data-testid="ledger-prev">Sebelumnya</Button>
          <Button size="sm" variant="outline" disabled={page >= (d.pages || 1)} onClick={() => setPage((p) => p + 1)} className="border-taupe text-cocoa" data-testid="ledger-next">Berikutnya</Button>
        </div>
      </div>
    </Panel>
  );
}

function UnsettledTab() {
  const [q, setQ] = useState("");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [data, setData] = useState({ total: 0, pages: 0, items: [] });
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/finance/unsettled", { params: { days: 30, q: q || undefined, date_from: dfrom || undefined, date_to: dto || undefined, page, limit } })
        .then((r) => setData(r.data)).catch(() => {});
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [q, dfrom, dto, page, limit]);
  useEffect(() => { setPage(1); }, [q, dfrom, dto]);

  return (
    <Panel>
      <div className="flex items-start gap-2 mb-3 text-sm text-cocoa">
        <AlertTriangle className="w-4 h-4 text-status-review mt-0.5" />
        <span>Penjualan online <b>&gt; 30 hari</b> yang belum muncul di settlement mana pun. Cek apakah ini retur yang belum kembali dari ekspedisi.</span>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari Order ID / No. Sale / customer..." className="w-72 bg-white" data-testid="unsettled-search" />
        <DateRangeFilter from={dfrom} setFrom={setDfrom} to={dto} setTo={setDto} showBasis={false} idp="unsettled" />
        <span className="ml-auto text-sm text-wmuted" data-testid="unsettled-total">{data.total} belum settle</span>
      </div>
      {data.items.length === 0 ? (
        <div className="text-center py-10 text-wmuted">Tidak ada. Semua penjualan online sudah ter-settle 👍</div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>No. Sale</TableHead><TableHead>Order ID</TableHead><TableHead>Marketplace</TableHead>
                <TableHead>Customer</TableHead><TableHead className="text-right">Total</TableHead>
                <TableHead>Tgl</TableHead><TableHead className="text-right">Umur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((s) => (
                <TableRow key={s.id} data-testid={`unsettled-row-${s.id}`}>
                  <TableCell className="font-medium text-cocoa">{s.number}</TableCell>
                  <TableCell className="text-sm">{s.order_id}</TableCell>
                  <TableCell className="text-sm">{s.marketplace || "-"}</TableCell>
                  <TableCell className="text-sm">{s.customer_name || "-"}</TableCell>
                  <TableCell className="text-right">{fmtIDR(s.grand_total)}</TableCell>
                  <TableCell className="text-sm">{fmtDateShort(s.created_at)}</TableCell>
                  <TableCell className="text-right"><Badge variant="outline" className="border-status-review text-status-review">{s.days_old} hari</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-wmuted">Halaman {data.page} / {data.pages || 1} · {data.total} data</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-taupe text-cocoa" data-testid="unsettled-prev">Sebelumnya</Button>
              <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage((p) => p + 1)} className="border-taupe text-cocoa" data-testid="unsettled-next">Berikutnya</Button>
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}

function SettlementDetail({ detail, setDetail, onChanged }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => { setRows(detail?.rows || []); }, [detail]);
  useEffect(() => { setFilter("all"); }, [detail?.batch?.id]);
  if (!detail) return null;
  const b = detail.batch;
  const shown = filter === "all" ? rows
    : filter === "cancelled" ? rows.filter((r) => r.row_flag === "cancelled")
    : filter === "unmatched" ? rows.filter((r) => r.match_status === "unmatched" && r.row_flag !== "cancelled")
    : rows.filter((r) => r.match_status === filter && r.row_flag !== "cancelled");
  const unmatchedCount = rows.filter((r) => r.match_status === "unmatched" && r.row_flag !== "cancelled").length;
  const cancelledCount = rows.filter((r) => r.row_flag === "cancelled").length;

  const exportRows = async (status) => {
    try {
      const r = await api.get(`/finance/settlements/${b.id}/export`, { params: { status }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `settlement-${status}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export gagal"); }
  };

  const saveHpp = async (row, val) => {
    const hpp = parseInt(val || 0, 10) || 0;
    if (hpp === (row.hpp || 0)) return;
    try {
      await api.put(`/finance/settlements/${b.id}/rows/${row.id}`, { hpp });
      const r = await api.get(`/finance/settlements/${b.id}`);
      setRows(r.data.rows);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const flagRow = async (row, cancel) => {
    try {
      await api.post(`/finance/settlements/${b.id}/rows/${row.id}/flag`, { cancelled: cancel });
      const r = await api.get(`/finance/settlements/${b.id}`);
      setDetail(r.data);
      setRows(r.data.rows);
      onChanged();
      toast.success(cancel ? "Ditandai batal" : "Flag batal dilepas");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rematch = async () => {
    try {
      await api.post(`/finance/settlements/${b.id}/rematch`);
      const r = await api.get(`/finance/settlements/${b.id}`);
      setDetail(r.data);
      setRows(r.data.rows);
      onChanged();
      toast.success("Dicocokkan ulang dengan data Sales");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="settlement-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle>{b.filename}</DialogTitle>
              <DialogDescription>Periode cair {fmtDateShort(b.period_start)} — {fmtDateShort(b.period_end)} · {b.row_count} transaksi</DialogDescription>
            </div>
            <Button size="sm" variant="outline" onClick={rematch} data-testid="rematch-btn" className="shrink-0 mr-6">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Cocokkan Ulang
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
          <Stat label="Penjualan" value={fmtIDR(b.totals.gross)} />
          <Stat label="Total Fee" value={"-" + fmtIDR(b.totals.fee)} tone="text-red-500" />
          <Stat label="Nett Cair" value={fmtIDR(b.totals.nett)} />
          <Stat label="Total HPP" value={fmtIDR(b.totals.hpp)} />
          <Stat label="Profit" value={fmtIDR(b.totals.profit)} tone={b.totals.profit >= 0 ? "text-status-passed" : "text-red-500"} />
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-white" data-testid="settlement-detail-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="sdfilter-all">Semua ({rows.length})</SelectItem>
              <SelectItem value="matched" data-testid="sdfilter-matched">Cocok ({rows.length - unmatchedCount})</SelectItem>
              <SelectItem value="unmatched" data-testid="sdfilter-unmatched">Tak Cocok ({unmatchedCount})</SelectItem>
              <SelectItem value="cancelled" data-testid="sdfilter-cancelled">Dibatalkan ({cancelledCount})</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-wmuted">{shown.length} baris ditampilkan</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportRows("unmatched")} disabled={!unmatchedCount} className="border-status-review text-status-review" data-testid="export-unmatched-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Tak Cocok
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportRows("all")} className="border-status-passed text-status-passed" data-testid="export-all-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Semua
            </Button>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Order ID</TableHead>
              <TableHead>Produk</TableHead>
              <TableHead className="text-right">Kotor</TableHead>
              <TableHead className="text-right">Fee</TableHead>
              <TableHead className="text-right">Nett</TableHead>
              <TableHead className="text-right">HPP</TableHead>
              <TableHead className="text-right">Profit</TableHead>
              <TableHead className="text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.id} data-testid={`srow-${r.id}`}>
                <TableCell className="text-sm">
                  {r.order_id}
                  {r.matched_sale_number && <div className="text-[11px] text-wmuted">→ {r.matched_sale_number}</div>}
                </TableCell>
                <TableCell className="text-sm max-w-[240px] truncate" title={r.matched_products || r.product_name}>{r.matched_products || r.product_name || "-"}</TableCell>
                <TableCell className="text-right text-sm">{fmtIDR(r.gross)}</TableCell>
                <TableCell className="text-right text-sm text-red-500" title={Object.entries(r.fees || {}).filter(([, v]) => v).map(([k, v]) => `${FEE_LABEL[k] || k}: ${fmtIDR(v)}`).join("\n")}>
                  -{fmtIDR(r.total_fee)}
                </TableCell>
                <TableCell className="text-right text-sm font-medium">{fmtIDR(r.nett)}</TableCell>
                <TableCell className="text-right">
                  <Input
                    key={`${r.id}-${r.hpp}`}
                    type="number"
                    defaultValue={r.hpp || 0}
                    onBlur={(e) => saveHpp(r, e.target.value)}
                    className="h-8 w-28 text-right text-sm ml-auto"
                    data-testid={`hpp-input-${r.id}`}
                  />
                </TableCell>
                <TableCell className={`text-right text-sm font-semibold ${r.profit >= 0 ? "text-status-passed" : "text-red-500"}`}>
                  <span className="inline-flex items-center gap-1 justify-end">
                    {r.profit >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {fmtIDR(r.profit)} <span className="text-[10px] text-wmuted">({r.margin_pct}%)</span>
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  {r.row_flag === "cancelled"
                    ? <div className="flex flex-col items-center gap-1">
                        <Badge variant="outline" className="border-wmuted text-wmuted">Dibatalkan</Badge>
                        <button onClick={() => flagRow(r, false)} className="text-[10px] text-blue-600 underline" data-testid={`unflag-${r.id}`}>Batalkan flag</button>
                      </div>
                    : r.match_status === "matched"
                      ? <Badge className="bg-status-passed/15 text-status-passed border-0">Cocok</Badge>
                      : <div className="flex flex-col items-center gap-1">
                          <Badge variant="outline" className="border-status-review text-status-review">Tak cocok</Badge>
                          <button onClick={() => flagRow(r, true)} className="text-[10px] text-blue-600 underline" data-testid={`flag-${r.id}`}>Tandai Batal</button>
                        </div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}


function SettlementSettingsDialog({ onClose, onSaved }) {
  const [start, setStart] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/finance/config").then((r) => setStart(r.data.settlement_start_date || "")).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/finance/config", { settlement_start_date: start });
      toast.success("Pengaturan disimpan");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="settlement-settings-dialog">
        <DialogHeader>
          <DialogTitle>Pengaturan Settlement</DialogTitle>
          <DialogDescription>Transaksi online sebelum tanggal ini tidak dihitung sebagai "Belum Settle" (mis. data sebelum ERP dipakai).</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <label className="text-sm text-wmuted">Tanggal Mulai Settlement</label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white mt-1" data-testid="settlement-start-input" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="settlement-cfg-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
